// components/UserProfile.js - Page de profil utilisateur améliorée
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { Home, User, Mail, Phone, Building, Lock, Camera, Clock, Calendar, TrendingUp, Award, FileText, QrCode, Edit2, Save, X, Search, Users, Eye, EyeOff, Loader2 } from 'lucide-react';

// Search colleague panel - uses native <details> to avoid React insertBefore DOM errors
const SearchColleaguePanel = React.memo(({ searchQuery, searchResults, searching, onQueryChange, t }) => (
  <div style={{ marginTop: '20px' }}>
    <div style={{ position: 'relative', marginBottom: '20px' }}>
      <Search
        size={20}
        style={{
          position: 'absolute',
          left: '15px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'rgba(255,255,255,0.5)'
        }}
      />
      <input
        type="text"
        placeholder={t('profile.search_placeholder')}
        value={searchQuery}
        onChange={(e) => onQueryChange(e.target.value)}
        style={{
          width: '100%',
          padding: '12px 12px 12px 45px',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '12px',
          color: 'white',
          fontSize: '16px'
        }}
      />
      {searching && (
        <div style={{
          position: 'absolute',
          right: '15px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTop: '2px solid #14b8a6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
    </div>

    {searchResults.length > 0 ? (
      <div style={{ display: 'grid', gap: '15px' }}>
        {searchResults.map((colleague, index) => (
          <div
            key={colleague.id || `colleague-${index}`}
            style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(255,255,255,0.1)',
              transition: 'all 0.3s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: colleague.photo_url
                  ? `url(${colleague.photo_url.startsWith('http') ? colleague.photo_url : API_BASE + colleague.photo_url}) center/cover`
                  : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 700,
                color: '#ffffff',
                flexShrink: 0,
                border: '2px solid rgba(255,255,255,0.2)'
              }}>
                {!colleague.photo_url && (
                  `${colleague.first_name?.[0] || ''}${colleague.last_name?.[0] || ''}`.toUpperCase()
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff' }}>
                    {colleague.first_name} {colleague.last_name}
                  </span>
                  <span
                    title={colleague.is_present ? (t('profile.present') || 'Present') : (t('profile.absent') || 'Absent')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: colleague.is_present ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                      color: colleague.is_present ? '#4ade80' : '#f87171',
                      border: `1px solid ${colleague.is_present ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`
                    }}
                  >
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: colleague.is_present ? '#22c55e' : '#ef4444',
                        flexShrink: 0
                      }}
                    />
                    {colleague.is_present ? (t('profile.present') || 'Present') : (t('profile.absent') || 'Absent')}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                  {colleague.employee_code && (
                    <span><User size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{t('profile.colleague_code')}: {colleague.employee_code}</span>
                  )}
                  {colleague.email && (
                    <span><Mail size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{colleague.email}</span>
                  )}
                  {colleague.phone && (
                    <span><Phone size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{colleague.phone}</span>
                  )}
                  {colleague.department && (
                    <span><Building size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{colleague.department}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : searchQuery.length >= 2 && !searching ? (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.6)', fontSize: '16px' }}>
        <Users size={48} style={{ marginBottom: '15px', opacity: 0.5 }} />
        <div>{t('profile.no_colleague_found').replace('{{query}}', searchQuery)}</div>
      </div>
    ) : searchQuery.length < 2 ? (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
        <Search size={32} style={{ marginBottom: '10px', opacity: 0.5 }} />
        <div>{t('profile.search_min_chars')}</div>
      </div>
    ) : null}
  </div>
));

const UserProfile = ({ user: initialUser, onNavigate }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(initialUser);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [stats, setStats] = useState({
    totalHours: null,
    leaveBalance: null,
    pendingRequests: null,
    documentsCount: null
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department: '',
    password: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadStats = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setStatsLoading(true);

    // Hours this month: same logic as dashboard — attendance_records (first in / last out per day)
    try {
      const attRes = await fetch(`${API_BASE}/api/attendance/my-dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (attRes.ok) {
        const attData = await attRes.json();
        const h = attData.hoursWorked != null ? Number(attData.hoursWorked) : 0;
        const rounded = Number.isFinite(h) ? Math.round(h * 10) / 10 : 0;
        setStats((prev) => ({ ...prev, totalHours: rounded }));
      } else {
        setStats((prev) => ({ ...prev, totalHours: 0 }));
      }
    } catch (e) {
      console.error('Profile stats: attendance hours', e);
      setStats((prev) => ({ ...prev, totalHours: 0 }));
    }

    // Leave balance: employee_leave_balances (current year) — use API totals even if balances array is empty
    try {
      const leavesResponse = await fetch(`${API_BASE}/api/leaves/my-balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (leavesResponse.ok) {
        const leavesData = await leavesResponse.json();
        let totalBalance = 0;
        if (leavesData.paid_remaining != null) {
          totalBalance = Number(leavesData.paid_remaining);
        } else if (leavesData.total_remaining != null) {
          totalBalance = Number(leavesData.total_remaining);
        } else if (leavesData.balances?.length > 0) {
          const paid = (leavesData.balances || []).filter((b) => b.is_paid === true || b.is_paid === 't');
          const src = paid.length > 0 ? paid : leavesData.balances;
          totalBalance = src.reduce((sum, b) => sum + Number(b.remaining_days || 0), 0);
        }
        if (!Number.isFinite(totalBalance)) totalBalance = 0;
        setStats((prev) => ({ ...prev, leaveBalance: Math.round(totalBalance * 100) / 100 }));
      } else {
        setStats((prev) => ({ ...prev, leaveBalance: 0 }));
      }
    } catch (e) {
      console.error('Profile stats: leave balances', e);
      setStats((prev) => ({ ...prev, leaveBalance: 0 }));
    }

    // Pending leave requests
    try {
      const requestsResponse = await fetch(`${API_BASE}/api/leaves/my-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (requestsResponse.ok) {
        const requestsData = await requestsResponse.json();
        setStats((prev) => ({
          ...prev,
          pendingRequests: Array.isArray(requestsData.requests) ? requestsData.requests.length : 0
        }));
      } else {
        setStats((prev) => ({ ...prev, pendingRequests: 0 }));
      }
    } catch (e) {
      console.error('Profile stats: pending requests', e);
      setStats((prev) => ({ ...prev, pendingRequests: 0 }));
    }

    // Documents visible to this user (same list as Documents module)
    try {
      const docRes = await fetch(`${API_BASE}/api/documents`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (docRes.ok) {
        const docData = await docRes.json();
        const list = docData.documents;
        const n = Array.isArray(list) ? list.length : 0;
        setStats((prev) => ({ ...prev, documentsCount: n }));
      } else {
        setStats((prev) => ({ ...prev, documentsCount: 0 }));
      }
    } catch (e) {
      console.error('Profile stats: documents', e);
      setStats((prev) => ({ ...prev, documentsCount: 0 }));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserProfile();
    loadEmployeeData();
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') loadStats();
    };
    window.addEventListener('focus', loadStats);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', loadStats);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadStats]);

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          setUser(data.user);
          setFormData({
            first_name: data.user.first_name || '',
            last_name: data.user.last_name || '',
            email: data.user.email || '',
            phone: data.user.phone || '',
            department: data.user.department || '',
            password: '',
            confirmPassword: ''
          });
        }
      }
    } catch (error) {
      console.error('Erreur chargement profil:', error);
      setMessage('❌ ' + t('profile.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/employees/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.employee) {
          setEmployee(data.employee);
        }
      }
    } catch (error) {
      console.error('Erreur chargement données employé:', error);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('❌ ' + t('profile.error_photo_size'));
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('❌ ' + t('profile.error_photo_type'));
        return;
      }
      setSelectedPhoto(file);
      handlePhotoUpload(file);
    }
  };

  const handlePhotoUpload = async (file) => {
    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      let csrfToken = '';
      try {
        const csrfRes = await fetch(`${API_BASE}/api/auth/csrf-token`, { method: 'GET' });
        if (csrfRes.ok) {
          const csrfData = await csrfRes.json();
          csrfToken = csrfData.csrfToken || '';
        }
      } catch (e) { /* ignore */ }
      if (!csrfToken) {
        setMessage('❌ ' + (t('profile.error_connection') || 'Please refresh the page and try again.'));
        setUploadingPhoto(false);
        setSelectedPhoto(null);
        return;
      }
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`${API_BASE}/api/auth/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, photo_url: data.photo_url }));
        setMessage('✅ ' + t('profile.success_photo_updated'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage('❌ ' + (error.error || t('profile.error_upload')));
      }
    } catch (error) {
      console.error('Erreur upload photo:', error);
      setMessage('❌ ' + t('profile.error_connection'));
    } finally {
      setUploadingPhoto(false);
      setSelectedPhoto(null);
    }
  };

  const searchColleagues = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/employees/search?q=${encodeURIComponent(query.trim())}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.employees || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Erreur recherche collègues:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim().length >= 2) {
      searchColleagues(value);
    } else {
      setSearchResults([]);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage('');

    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage('❌ ' + t('profile.error_password_mismatch'));
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setMessage('❌ ' + t('profile.error_password_min_length'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const updateData = {};

      if (formData.phone && formData.phone.trim()) {
        updateData.phone = formData.phone.trim();
      }

      if (formData.password) {
        updateData.password = formData.password;
      }

      if (Object.keys(updateData).length === 0) {
        setMessage('❌ ' + t('profile.error_no_changes'));
        return;
      }

      let csrfToken = '';
      try {
        const csrfRes = await fetch(`${API_BASE}/api/auth/csrf-token`, { method: 'GET' });
        if (csrfRes.ok) {
          const csrfData = await csrfRes.json();
          csrfToken = csrfData.csrfToken || '';
        }
      } catch (e) { /* ignore */ }
      if (!csrfToken) {
        setMessage('❌ ' + (t('profile.error_connection') || 'Please refresh the page and try again.'));
        return;
      }

      setSaving(true);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          ...(userTimezone ? { 'X-User-Timezone': userTimezone } : {})
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const data = await response.json();
        setMessage('✅ ' + t('profile.success_profile_updated'));
        setEditing(false);
        await loadUserProfile();
        setTimeout(() => setMessage(''), 3000);
      } else {
        let errMsg = t('profile.error_update');
        try {
          const errorData = await response.json();
          console.error('Profile update error:', response.status, errorData);
          errMsg = errorData.error || errMsg;
        } catch (parseErr) {
          console.error('Profile update error (non-JSON):', response.status, response.statusText);
        }
        setMessage('❌ ' + errMsg);
      }
    } catch (error) {
      console.error('Profile update network error:', error);
      setMessage('❌ ' + t('profile.error_connection'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>
            {t('profile.loading')}
          </div>
        </div>
      </div>
    );
  }

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '👤';
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('profile.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(28px, 5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <User size={32} />
            {t('profile.title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('profile.subtitle')}
        </p>
      </div>

      {/* Contenu principal */}
      <div className="profile-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Message */}
        {message && (
          <div style={{
            padding: '15px',
            background: message.includes('✅') ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            border: `2px solid ${message.includes('✅') ? '#10b981' : '#ef4444'}`,
            borderRadius: '12px',
            marginBottom: '20px',
            textAlign: 'center',
            fontWeight: 'bold',
            animation: 'slideDown 0.3s ease-out'
          }}>
            {message}
          </div>
        )}

        {/* Photo de profil et informations principales */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '20px',
          padding: '30px',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              background: user?.photo_url 
                ? `url(${API_BASE}${user.photo_url}) center/cover`
                : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '56px',
              margin: '0 auto 20px',
              boxShadow: '0 8px 24px rgba(20, 184, 166, 0.4)',
              border: '4px solid rgba(255, 255, 255, 0.2)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}>
              {!user?.photo_url && getInitials()}
            </div>
            <label style={{
              position: 'absolute',
              bottom: '10px',
              right: 'calc(50% - 70px + 10px)',
              background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(20, 184, 166, 0.4)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                style={{ display: 'none' }}
                disabled={uploadingPhoto}
              />
              {uploadingPhoto ? (
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              ) : (
                <Camera size={20} color="white" />
              )}
            </label>
          </div>
          
          <h2 style={{ color: '#ffffff', margin: '10px 0', fontSize: '28px', fontWeight: 700 }}>
            {user?.first_name} {user?.last_name}
          </h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '16px', marginBottom: '10px' }}>
            {user?.email}
          </p>
          {employee?.employee_code && (
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginBottom: '10px' }}>
              {t('profile.code')}: {employee.employee_code}
            </p>
          )}
          <div style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: user?.role === 'admin' 
              ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
              : user?.role === 'manager' 
              ? 'linear-gradient(135deg, #f59e0b, #d97706)' 
              : 'linear-gradient(135deg, #10b981, #059669)',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: 600,
            marginTop: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}>
            {user?.role === 'admin' ? t('profile.role_admin') : user?.role === 'manager' ? t('profile.role_manager') : t('profile.role_employee')}
          </div>
        </div>

        {/* Statistiques */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '15px',
            padding: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center'
          }}>
            <Clock size={24} color="#14b8a6" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
              {statsLoading || stats.totalHours == null
                ? <Loader2 size={22} className="spin" />
                : `${stats.totalHours}h`}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{t('profile.hours_this_month')}</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '15px',
            padding: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center'
          }}>
            <Calendar size={24} color="#06b6d4" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
              {statsLoading || stats.leaveBalance == null
                ? <Loader2 size={22} className="spin" />
                : stats.leaveBalance}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{t('profile.leave_days')}</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '15px',
            padding: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center'
          }}>
            <FileText size={24} color="#f59e0b" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
              {statsLoading || stats.pendingRequests == null
                ? <Loader2 size={22} className="spin" />
                : stats.pendingRequests}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{t('profile.pending_requests')}</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '15px',
            padding: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center'
          }}>
            <Award size={24} color="#8b5cf6" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
              {statsLoading || stats.documentsCount == null
                ? <Loader2 size={22} className="spin" />
                : stats.documentsCount}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{t('profile.documents')}</div>
          </div>
        </div>

        {/* Informations personnelles */}
        {!editing ? (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '20px',
            padding: '30px',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>{t('profile.personal_info')}</h3>
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
              >
                <Edit2 size={16} />
                {t('profile.edit')}
              </button>
            </div>

            <div style={{ display: 'grid', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <User size={20} color="rgba(255,255,255,0.6)" />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '4px' }}>{t('profile.first_name')}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
                    {user?.first_name || t('profile.not_provided')}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <User size={20} color="rgba(255,255,255,0.6)" />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '4px' }}>{t('profile.last_name')}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
                    {user?.last_name || t('profile.not_provided')}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <Mail size={20} color="rgba(255,255,255,0.6)" />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '4px' }}>{t('profile.email')}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
                    {user?.email}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <Phone size={20} color="rgba(255,255,255,0.6)" />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '4px' }}>{t('profile.phone')}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
                    {user?.phone || employee?.phone || t('profile.not_provided')}
                  </div>
                </div>
              </div>

              {(user?.company || user?.companyName || employee) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <Building size={20} color="rgba(255,255,255,0.6)" />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '4px' }}>{t('profile.company')}</div>
                    <div style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
                      {user?.company || user?.companyName || '—'}
                    </div>
                  </div>
                </div>
              )}

              {employee && (employee.administration || employee.department || employee.service || employee.grade) && (
                <div style={{
                  padding: '16px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '10px', fontWeight: 600 }}>
                    {t('profile.administration')}
                  </div>
                  <div style={{ display: 'grid', gap: '8px', fontSize: '14px', color: 'white' }}>
                    {employee.administration && <div><span style={{ color: 'rgba(255,255,255,0.6)' }}>{t('profile.administration_label')} </span>{employee.administration}</div>}
                    {employee.department && <div><span style={{ color: 'rgba(255,255,255,0.6)' }}>{t('profile.department_label')} </span>{employee.department}</div>}
                    {employee.service && <div><span style={{ color: 'rgba(255,255,255,0.6)' }}>{t('profile.service_label')} </span>{employee.service}</div>}
                    {employee.bureau && <div><span style={{ color: 'rgba(255,255,255,0.6)' }}>{t('profile.bureau_label')} </span>{employee.bureau}</div>}
                    {employee.grade && <div><span style={{ color: 'rgba(255,255,255,0.6)' }}>{t('profile.grade_label')} </span>{employee.grade}</div>}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <Calendar size={20} color="rgba(255,255,255,0.6)" />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '4px' }}>{t('profile.registration_date')}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString(navigator.language || 'fr-FR', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : t('profile.not_provided')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '20px',
            padding: '30px',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>{t('profile.edit_my_info')}</h3>
            </div>

            <div style={{ display: 'grid', gap: '20px' }}>
              {/* Informations non modifiables (en lecture seule) */}
              <div style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '15px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '12px'
                }}>
                  <Lock size={16} />
                  <span>{t('profile.admin_managed_info')}</span>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    {t('profile.first_name')}
                  </label>
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '16px',
                    cursor: 'not-allowed'
                  }}>
                    {formData.first_name || t('profile.not_provided')}
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    {t('profile.last_name')}
                  </label>
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '16px',
                    cursor: 'not-allowed'
                  }}>
                    {formData.last_name || t('profile.not_provided')}
                  </div>
                </div>

                <div>
                  <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    {t('profile.email')}
                  </label>
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '16px',
                    cursor: 'not-allowed'
                  }}>
                    {formData.email}
                  </div>
                </div>
              </div>

              {/* Informations modifiables */}
              <div>
                <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  {t('profile.phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <Lock size={20} color="#94a3b8" />
                  <label style={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}>
                    {t('profile.change_password_optional')}
                  </label>
                </div>
                <div style={{ marginBottom: '15px', position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('profile.new_password_placeholder')}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      paddingRight: '45px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '16px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                  </button>
                </div>
                {formData.password && (
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={t('profile.confirm_password_placeholder')}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        paddingRight: '45px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: 'white',
                        fontSize: '16px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showConfirmPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '15px',
                    background: saving ? 'rgba(20, 184, 166, 0.6)' : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: saving ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(20, 184, 166, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(20, 184, 166, 0.3)';
                  }}
                >
                  {saving ? <Loader2 size={18} style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
                  {saving ? (t('profile.processing') || 'Processing…') : t('profile.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setMessage('');
                    loadUserProfile();
                  }}
                  style={{
                    flex: 1,
                    padding: '15px',
                    background: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  }}
                >
                  <X size={18} />
                  {t('profile.cancel')}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Section Recherche de collègues - native <details> avoids React insertBefore bug */}
        <details
          style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '20px',
            padding: '30px',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '20px'
          }}
          onToggle={(e) => {
            if (!e.currentTarget.open) {
              setSearchQuery('');
              setSearchResults([]);
            }
          }}
        >
          <summary style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            listStyle: 'none',
            userSelect: 'none'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Users size={24} color="#14b8a6" />
              <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>{t('profile.search_colleague')}</h3>
            </div>
            <span style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 16px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Search size={16} />
              {t('profile.search')}
            </span>
          </summary>
          <SearchColleaguePanel
            searchQuery={searchQuery}
            searchResults={searchResults}
            searching={searching}
            onQueryChange={(value) => {
              setSearchQuery(value);
              if (value.trim().length >= 2) searchColleagues(value);
              else setSearchResults([]);
            }}
            t={t}
          />
        </details>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .profile-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .profile-content {
            padding: 180px 60px 60px !important;
          }
        }
        .profile-content {
          animation: slideUp 0.4s ease-out;
        }
        details summary::-webkit-details-marker { display: none; }
        details summary::marker { display: none; }
      `}</style>
    </div>
  );
};

export default UserProfile;
